import React, { useState, useEffect } from 'react';
import { requestsApi } from '../services/api';
import Navbar from '../components/Navbar';
import RequestsList from '../components/RequestsList';
import RequestForm from '../components/RequestForm';
import ConfirmModal from '../components/ConfirmModal';

const Research = () => {
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await requestsApi.getAll();
      setRequests(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (values) => {
    try {
      if (editing) {
        await requestsApi.update(editing.id, values);
      } else {
        await requestsApi.create(values);
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.details?.join(', ') || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    setConfirmId(null);
    try {
      await requestsApi.delete(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleEdit = (req) => {
    setEditing(req);
    setShowForm(true);
  };

  const handleRunNow = async (id) => {
    try {
      await requestsApi.runNow(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Run now failed');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {confirmId && (
        <ConfirmModal
          message="Delete this research request?"
          onConfirm={() => handleDelete(confirmId)}
          onCancel={() => setConfirmId(null)}
        />
      )}
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Research Requests</h1>
          <button
            onClick={() => setShowForm(true)}
            className="w-10 h-10 bg-primary-600 text-white rounded-full shadow-md hover:bg-primary-700 transition flex items-center justify-center"
            title="New Request"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 cursor-pointer text-sm"
            onClick={() => setError('')}
          >
            {error}
          </div>
        )}

        {showForm && (
          <RequestForm
            initial={editing}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <RequestsList
            requests={requests}
            onEdit={handleEdit}
            onDelete={(id) => setConfirmId(id)}
            onRunNow={handleRunNow}
          />
        )}
      </div>
    </div>
  );
};

export default Research;
